<main id="main-content" class="h-screen flex flex-col pl-10 py-4 pr-4 transition-all duration-0">
    <div class="w-full flex flex-col p-2 items-center gap-2">
        <div class="w-full flex flex-row items-center justify-between">
            <div class="w-full flex gap-2 items-center justify-start">
                <h1 class="oswald-font text-3xl uppercase">
                    Incident Command System Roles
                </h1>
                <div data-popover-target="popover-bottom" data-popover-placement="bottom" type="button">
                    <lucide-icon [name]="icons.CircleHelp" [size]=24 class="text-red-500"></lucide-icon>
                </div>
                <div data-popover id="popover-bottom" role="tooltip" class="absolute z-30 invisible inline-block w-fit max-w-[600px] text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-md shadow-xs opacity-0">
                    <div class="px-3 py-2">
                        <p>
                            Assign the contacts listed in the <strong>Contacts</strong> list to positions in the <strong>NIMS & ICS</strong> structure. Each position requires a primary and two alternates to be assigned. The same person cannot be assigned to the primary role and as an alternate to the same position.
                        </p>
                        <p>
                            To assign a person to a position, click on the position on the right side of the screen. It will be highlighted orange when selected. Click on 
                            <span class="inline-flex rounded-md shadow-sm gap-[2px] text-xs" role="group">
                                <span class="inline-flex p-2 rounded-s-md text-white bg-gradient-to-b from-nside-orange-100 to-nside-orange-200">
                                    Primary
                                </span>
                                <span class="inline-flex p-2 rounded-e-md text-white bg-gradient-to-b from-nside-gray-100 to-nside-gray-200">
                                    Alternate
                                </span>
                            </span> to assign the person to either the primary or alternate for this position. 
                        </p>
                        <p>
                            To remove a person as the primary or alternate for a position, simply click on the person you would like to remove from that position in the position assignment table. 
                        </p>
                        <p>
                            To save the position assignment table, click on <span class="inline-flex px-3.5 py-1 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md">
                                Save
                            </span> when all necessary edits have been made. To clear all positions (does not delete), click on <span 
                            class="inline-flex w-8 h-8 rounded border-2 border-nside-gray-200 text-nside-gray-200 flex items-center justify-center"
                        >
                            <lucide-icon [img]="icons.Eraser" size="20"></lucide-icon>
                        </span> . This will remove all persons that are currently assigned, but it will not delete any assignments. To delete all assignments, click on 
                        <span class="inline-flex w-8 h-8 flex items-center justify-center text-white bg-gradient-to-b from-nside-gray-300 to-nside-gray-400 rounded">
                            <lucide-icon [img]="icons.Trash" size="20"></lucide-icon>
                        </span> . This will permanently delete any contact's assignment in the <strong>NIMS & ICS</strong> page.
                        </p>
                    </div>
                    <div data-popper-arrow></div>
                </div>
            </div>
            <eop-assistant
                [sectionId]="sectionType"
            ></eop-assistant>          
        </div>
    </div>
    
    <!-- MAIN CONTENT -->
    <div class="p-2 w-full flex flex-grow flex-row items-start gap-8 overflow-y-auto">
        <div class="w-1/3 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-4">
            <div class="flex flex-row w-full items-center justify-between gap-2">
                <h2 class="oswald-font text-3xl font-medium w-1/2">CONTACTS</h2>
                <div class="relative w-1/2">
                    <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                        <lucide-icon [name]="icons.SearchIcon" [size]="20"></lucide-icon>
                    </div>
                    <input 
                        type="search"
                        [(ngModel)]="searchQuery"
                        (input)="filterContacts()"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2"
                        placeholder="Search" 
                        required />
                </div>
            </div>
            <div class="h-full w-full flex flex-wrap text-gray-900 overflow-y-auto">
                <div class="h-full w-full mr-2 flex flex-col gap-2">
                    <ng-container *ngIf="contactsList.length > 0; else emptyContact">                        
                        <div 
                            *ngFor="let contact of contactsList"
                            class="w-full flex flex-row items-stretch gap-2"
                        >
                            <button
                                class="min-h-10 flex-1 px-2.5 py-1.5 flex flex-row gap-2 items-center justify-between rounded-md bg-gray-50 border border-nside-gray-300 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200 hover:text-white hover:border-nside-orange-100 group"
                                (click)="$event.preventDefault(); openInfo(contact);"
                            >
                                <span class="text-start">{{ contact.name }}</span>
                            </button>

                            <div *ngIf="canSave() && selectedPositionId" class="inline-flex rounded-md shadow-sm gap-[2px] text-xs" role="group">
                                <button 
                                    type="button" 
                                    class="p-2 rounded-s-md text-white"
                                    [ngClass]="{
                                        'bg-gray-200 border-gray-300 cursor-not-allowed': hasPrimary(selectedPositionId) || isContactAlternate(contact.id+'', selectedPositionId),
                                        'bg-gradient-to-b from-nside-orange-100 to-nside-orange-200 hover:from-nside-orange-100 hover:to-nside-orange-300 hover:border-nside-orange-400': !hasPrimary(selectedPositionId) && !isContactAlternate(contact.id+'', selectedPositionId)
                                    }"
                                    (click)="$event.preventDefault(); assignContact(contact.id+'', selectedPositionId, 1);"
                                    [disabled]="hasPrimary(selectedPositionId) || isContactAlternate(contact.id+'', selectedPositionId)"
                                >
                                    Primary
                                </button>

                                <button 
                                    type="button" 
                                    class="p-2 rounded-e-md text-white"
                                    [ngClass]="{
                                        'bg-gray-200 border-gray-300 cursor-not-allowed': hasTwoAlternates(selectedPositionId) || isContactPrimary(contact.id+'', selectedPositionId) || isContactAlternate(contact.id+'', selectedPositionId),
                                        'bg-gradient-to-b from-nside-gray-100 to-nside-gray-200 hover:from-nside-gray-100 hover:to-nside-gray-300 hover:border-nside-gray-100': !hasTwoAlternates(selectedPositionId) && !isContactPrimary(contact.id+'', selectedPositionId) && !isContactAlternate(contact.id+'', selectedPositionId)
                                    }"
                                    (click)="$event.preventDefault(); assignContact(contact.id+'', selectedPositionId, 2);"
                                    [disabled]="hasTwoAlternates(selectedPositionId) || isContactPrimary(contact.id+'', selectedPositionId) || isContactAlternate(contact.id+'', selectedPositionId)"
                                >
                                    Alternate
                                </button>

                            </div>                                
                        </div>
                    </ng-container>                             
                    <ng-template #emptyContact>
                        <div class="text-nside-gray-100 italic">
                            Empty list
                        </div>
                    </ng-template>
                </div>
            </div>                
        </div>
        <div class="w-2/3 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-4">
            <div class="w-full flex items-center justify-end gap-2">
                <button 
                    *ngIf="canSave()"
                    data-tooltip-target="tooltip-delete"  data-tooltip-placement="bottom"
                    class="w-8 h-8 rounded border-2 border-nside-gray-200 text-nside-gray-200 flex items-center justify-center"
                    [ngClass]="{
                        'hover:text-white hover:bg-nside-gray-200': selectedPositions.length > 0,
                        'cursor-not-allowed opacity-50': selectedPositions.length === 0
                    }"
                    (click)="$event.preventDefault(); selectedPositions = [];"
                    [disabled]="selectedPositions.length === 0"
                >
                    <lucide-icon [img]="icons.Eraser" size="20"></lucide-icon>
                </button>
                <div id="tooltip-delete" role="tooltip" class="absolute z-30 invisible inline-block px-3 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg shadow-sm opacity-0 tooltip">
                    Clear All Fields
                    <div class="tooltip-arrow" data-popper-arrow></div>
                </div>
                <button
                    *ngIf="canSave()" 
                    data-tooltip-target="tooltip-clear"  data-tooltip-placement="bottom"
                    class="w-8 h-8 flex items-center justify-center font-normal text-white oswald-font bg-gradient-to-b from-nside-gray-300 to-nside-gray-400 rounded"
                    [ngClass]="{
                        'hover:bg-gradient-to-b hover:from-red-600 hover:to-red-700 cursor-pointer': selectedPositions.length > 0,
                        'cursor-not-allowed opacity-50': selectedPositions.length === 0
                    }"
                    (click)="$event.preventDefault(); deleteConfirm = true;"
                    [disabled]="selectedPositions.length === 0"
                >
                    <lucide-icon [img]="icons.Trash" size="20"></lucide-icon>
                </button>
                <div id="tooltip-clear" role="tooltip" class="absolute z-30 invisible inline-block px-3 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg shadow-sm opacity-0 tooltip">
                    Delete All
                    <div class="tooltip-arrow" data-popper-arrow></div>
                </div>   
            </div>
            <div class="w-full flex flex-row items-center justify-center text-center oswald-font text-xl font-medium gap-2">
                <div class="w-1/4">
                    Position
                </div>
                <div class="w-1/4 text-nside-orange-400">
                    Primary
                </div>
                <div class="w-1/4">
                    Alternate
                </div>
                <div class="w-1/4">
                    Alternate
                </div>
            </div>
            <div class="w-full flex flex-col items-center justify-start mr-2 overflow-y-auto">
                <div 
                    *ngFor="let position of positionsList"
                    class="py-1 pl-1 pr-2 w-full flex flex-row items-stretch ustify-center gap-1"
                >
                    <div class="w-1/4 flex items-stretch">
                        <button 
                            class="flex-grow flex items-center justify-start h-full p-2 border border-nside-gray-300 rounded-md bg-white text-start oswald-font"
                            [ngClass]="{
                                'cursor-default': !canSave(),
                                'text-white bg-gradient-to-b from-nside-orange-100 to-nside-orange-200 border-nside-orange-100': selectedPositionId === position.id+'' && canSave()
                            }"
                            (click)="$event.preventDefault(); setSelectedPositionId(position.id + '');"
                            [disabled]="!canSave()"
                        >
                            {{ position.position_name }}
                        </button>
                    </div>
                    <div class="w-1/4 flex items-stretch">
                        <ng-container *ngIf="getPrimaryContact(position.id) as primaryContact; else noPrimary">
                            <button 
                                class="w-full h-full px-2.5 py-1.5 flex flex-row gap-2 items-center justify-between rounded-md bg-gray-50 border border-nside-orange-400"
                                [ngClass]="{
                                    'cursor-default': !canSave(),
                                    'hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200 hover:text-white hover:border-nside-orange-100': canSave()
                                }"
                                [disabled]="!canSave()"
                                (click)="$event.preventDefault(); removeContact(position.id, 1);"
                            >
                                <span class="text-start">{{ primaryContact.name }}</span>
                                <lucide-icon *ngIf="canSave()" [name]="icons.X" [size]="20" class=""></lucide-icon>
                            </button>
                        </ng-container>
                        <ng-template #noPrimary><span class="w-full text-center flex items-center justify-center">-</span></ng-template>
                    </div>
                    <div class="w-1/4 flex items-stretch">
                        <ng-container *ngIf="getAlternateContact(position.id, 2) as alternateContact1; else noAlt1">
                            <button 
                                class="w-full h-full px-2.5 py-1.5 flex flex-row gap-2 items-center justify-between rounded-md bg-gray-50 border border-nside-gray-300"
                                [ngClass]="{
                                    'cursor-default': !canSave(),
                                    'hover:bg-gradient-to-b hover:from-nside-gray-100 hover:to-nside-gray-200 hover:text-white hover:border-nside-gray-100': canSave()
                                }"
                                [disabled]="!canSave()"
                                (click)="$event.preventDefault(); removeContact(position.id, 2);"
                            >
                            <span class="text-start">{{ alternateContact1.name }}</span>
                            <lucide-icon *ngIf="canSave()" [name]="icons.X" [size]="20" class=""></lucide-icon>
                            </button>
                        </ng-container>
                        <ng-template #noAlt1><span class="w-full text-center flex items-center justify-center">-</span></ng-template>
                    </div>
                    <div class="w-1/4 flex items-stretch">
                        <ng-container *ngIf="getAlternateContact(position.id, 3) as alternateContact2; else noAlt2">
                            <button 
                                class="w-full h-full px-2.5 py-1.5 flex flex-row gap-2 items-center justify-between rounded-md bg-gray-50 border border-nside-gray-300"
                                [ngClass]="{
                                    'cursor-default': !canSave(),
                                    'hover:bg-gradient-to-b hover:from-nside-gray-100 hover:to-nside-gray-200 hover:text-white hover:border-nside-gray-100': canSave()
                                }"
                                [disabled]="!canSave()"
                                (click)="$event.preventDefault(); removeContact(position.id, 3);"
                            >
                                <span class="text-start">{{ alternateContact2.name }}</span>
                                <lucide-icon *ngIf="canSave()" [name]="icons.X" [size]="20" class=""></lucide-icon>
                            </button>
                        </ng-container>
                        <ng-template #noAlt2><span class="w-full text-center flex items-center justify-center">-</span></ng-template>
                    </div>
                </div>
            </div>
            <div class="flex items-center justify-end gap-2 oswald-font">
                <button 
                    *ngIf="canSave()"
                    class="w-20 flex items-center justify-center px-4 py-1.5 rounded-md border bg-gradient-to-b oswald-font text-white"
                    [ngClass]="{
                        'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': selectedPositions.length === 0,
                        'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200': selectedPositions.length > 0
                    }"
                    [disabled]="selectedPositions.length === 0"
                    (click)="$event.preventDefault(); saveSelectedPosition();"
                >
                    Save
                </button>
            </div>
        </div>
    </div>

    <!-- APPROVE DOCUMENT BUTTON -->
    <div *ngIf="!isGaPlan" class="w-full flex flex-col p-2 items-center gap-2">
        <div class="w-full bg-white rounded-md shadow-custom-2 p-6 flex gap-4 overflow-y-auto">
            <div
                class="w-full p-2 border-l-4 bg-gray-50 rounded-r-md flex items-end gap-2"
                [ngClass]="{
                    'border-green-600': allICSData[0]?.user_name_approved,
                    'border-nside-gray-300': !allICSData[0]?.user_name_approved
                }"
            >
                <div class="w-full flex flex-col items-start justify-between">
                    <p class="oswald-font font-normal">
                        Confirmed By: <span class="raleway-font">{{ allICSData[0]?.user_name_approved || 'Not Confirmed Yet' }}</span>
                    </p>
                    <p class="oswald-font font-normal">
                        Date Confirm: <span class="raleway-font">{{ allICSData[0]?.date_approved | date: 'MM/dd/yyyy - HH:mm' }}</span>
                    </p>
                    <p *ngIf="canSave()" class="w-full text-end">
                        By clicking this button, I certify that this information is correct and does not need to be updated.
                    </p>
                </div>
                <button
                    *ngIf="canSave()"
                    class="w-20 flex items-center justify-center text-white oswald-font px-4 py-1.5 bg-gradient-to-b rounded-md"
                    [ngClass]="{
                        'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': allICSData.length === 0,
                        'border-green-600 from-green-500 to-green-600 hover:from-green-500 hover:to-green-700': allICSData.length > 0
                    }"
                    [disabled]="allICSData.length === 0"
                    (click)="$event.preventDefault(); approveBuildingICS();"
                >
                    Confirm
                </button>
            </div>
        </div>
    </div>

    <div *ngIf="loading">
        <spinner></spinner>
    </div>
    
    <contact-info-popup
        *ngIf="contactInfo"
        [contact]="contactInfo"
    ></contact-info-popup>

   <delete-confirmation
        *ngIf="deleteConfirm"
        [text]="'all records'"
        [itemToDelete]="currentBuildingId"
        (onClose)="cancelDelete()"
        (onDelete)="deleteAllRecords($event)"
    ></delete-confirmation>

    <introduction></introduction>

    <prv-next-buttons></prv-next-buttons>

    <popup-message 
        [type]="messageModal"
        [message]="popupMessage"
        (close)="resetMessageModal()"
    ></popup-message>
</main>